import axios from "axios";
import { toast } from "sonner";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// -------- Auth token (localStorage fallback when cookies are blocked) --------
// iOS Safari ITP + Chrome-on-iOS sometimes drop SameSite=None cookies. Storing
// the session token in localStorage as a backup lets us attach it as a
// Bearer header so auth keeps working even if the cookie never persists.
const TOKEN_KEY = "resynote_session_token";

export function getSessionToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setSessionToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage may be disabled — cookie path still works */ }
}

export function clearSessionToken() {
  setSessionToken(null);
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Authorization header on every request if we have a token.
api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Trial-expired global handler: shows a toast and routes to /settings.
let lastTrialToast = 0;
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 402 && error.response?.data?.code === "TRIAL_EXPIRED") {
      const now = Date.now();
      if (now - lastTrialToast > 4000) {
        lastTrialToast = now;
        toast.error(
          error.response.data.detail ||
            "Your free trial has ended. Upgrade to ResyNote Premium to continue.",
          {
            action: {
              label: "Upgrade",
              onClick: () => {
                window.location.assign("/settings");
              },
            },
          }
        );
      }
    }
    return Promise.reject(error);
  }
);

export default api;
